/**
 * ================================================================
 * COMPONENT: FirebaseNotificationHandler
 * ================================================================
 *
 * @component        FirebaseNotificationHandler
 * @description      Invisible component that handles Firebase foreground messages
 * @category         System Component
 *
 * @props
 * - uid: string - User identifier for notifications
 * - enabled?: boolean - Whether to enable Firebase notifications
 *
 * @responsibilities
 * - Firebase foreground message handling
 * - Browser notification display
 * - Integration with existing notification system
 * - Silent operation (no UI rendering)
 *
 * @placement
 * This component should be placed in the main App or Layout component
 * It operates silently in the background without rendering any UI
 *
 * @usage
 * ```tsx
 * <FirebaseNotificationHandler
 *   uid={userData?.branch_code || 'VJ'}
 *   enabled={true}
 * />
 * ```
 * ================================================================
 */

import { useEffect } from 'react';

import { useFirebaseNotifications } from '@/hooks/useFirebaseNotifications';

interface FirebaseNotificationHandlerProps {
  /** User identifier for notifications */
  uid: string;
  /** Enable Firebase notification handling */
  enabled?: boolean;
  /** Enable automatic permission request */
  autoRequestPermission?: boolean;
}

export function FirebaseNotificationHandler({
  uid,
  enabled = true,
  autoRequestPermission = false,
}: FirebaseNotificationHandlerProps) {
  // ================================================================
  // FIREBASE NOTIFICATIONS HOOK
  // ================================================================
  //
  // Initialize Firebase notifications with foreground message handling
  // This will set up the browser notification display for FCM messages
  // ================================================================

  const {
    isInitialized,
    hasPermission,
    token,
    error,
    requestPermission,
    hasExceededRetryLimit,
    resetRetryCount,
  } = useFirebaseNotifications({
    config: {
      enableForegroundMessages: enabled,
      enableBackgroundMessages: enabled,
    },
    onForegroundMessage: (payload) => {
      console.log('🔔 Firebase notification received in handler:', payload);

      // ================================================================
      // FOREGROUND MESSAGE PROCESSING
      // ================================================================
      //
      // Purpose: Process Firebase messages when app is active
      // Integration: The actual notification display is handled by fcmTokenManager
      // Logging: Provide visibility into notification receipt
      // ================================================================

      // Log notification data for debugging
      if (payload.notification) {
        console.log('📧 Notification title:', payload.notification.title);
        console.log('📧 Notification body:', payload.notification.body);
      }

      if (payload.data) {
        console.log('📦 Notification data:', payload.data);
      }

      // Additional processing can be added here:
      // - Analytics tracking
      // - Custom sound effects
      // - Badge updates
      // - Deep linking preparation
    },
    // Disable auto-refresh and auto-generation; token should be generated explicitly on login
    autoRefresh: false,
  });

  // ================================================================
  // AUTO PERMISSION REQUEST
  // ================================================================
  //
  // Automatically request notification permission if enabled
  // Only runs once when component mounts and conditions are met
  // ================================================================

  useEffect(() => {
    async function handleAutoPermission() {
      if (
        autoRequestPermission &&
        enabled &&
        isInitialized &&
        !hasPermission &&
        'Notification' in window &&
        Notification.permission === 'default'
      ) {
        try {
          console.log('🔔 Auto-requesting notification permission...');
          await requestPermission();
        } catch (err) {
          console.warn('⚠️ Auto permission request failed:', err);
        }
      }
    }

    handleAutoPermission();
  }, [autoRequestPermission, enabled, isInitialized, hasPermission, requestPermission]);

  // ================================================================
  // DEVELOPMENT DEBUG UTILITIES
  // ================================================================
  //
  // Purpose: Provide debug utilities for developers in development mode
  // Access: Exposed as window functions for easy console access
  // Security: Only available in development builds
  // ================================================================

  useEffect(() => {
    if (import.meta.env.DEV) {
      // Expose FCM debug utilities to window object for development
      (window as any).resetFCMRetryCount = () => {
        console.log('🔄 Resetting FCM retry count...');
        resetRetryCount();
        console.log('✅ FCM retry count reset successfully');
      };

      (window as any).getFCMDebugInfo = () => {
        return {
          isInitialized,
          hasPermission,
          hasToken: !!token,
          tokenPreview: token ? token.substring(0, 50) + '...' : null,
          hasExceededRetryLimit,
          error,
          uid,
          enabled,
        };
      };
    }
  }, [
    isInitialized,
    hasPermission,
    token,
    hasExceededRetryLimit,
    error,
    uid,
    enabled,
    resetRetryCount,
  ]);

  // ================================================================
  // LOGGING AND DEBUGGING
  // ================================================================
  //
  // Provide visibility into Firebase notification status
  // Helps with debugging notification issues
  // ================================================================

  useEffect(() => {
    if (import.meta.env.DEV && enabled) {
      console.group('🔔 Firebase Notification Handler Status');
      console.log('UID:', uid);
      console.log('Enabled:', enabled);
      console.log('Initialized:', isInitialized);
      console.log('Has Permission:', hasPermission);
      console.log('FCM Token:', token ? '✅ Present' : '❌ Missing');
      console.log('Retry Limit Exceeded:', hasExceededRetryLimit ? '🚫 Yes' : '✅ No');
      console.log('Error:', error || 'None');

      // Add helpful debug commands
      if (hasExceededRetryLimit) {
        console.log('💡 To reset retry count, run: window.resetFCMRetryCount()');
      }

      console.groupEnd();
    }
  }, [uid, enabled, isInitialized, hasPermission, token, error, hasExceededRetryLimit]);

  // ================================================================
  // ERROR HANDLING
  // ================================================================
  //
  // Log errors for debugging but don't disrupt app functionality
  // Notifications are a nice-to-have feature, not critical
  // ================================================================

  useEffect(() => {
    if (error) {
      console.warn('⚠️ Firebase notification error:', error);

      // Optional: Send error to analytics or error reporting service
      // Example: errorReporting.reportError('firebase_notification_error', error);
    }
  }, [error]);

  // ================================================================
  // NO UI RENDERING
  // ================================================================
  //
  // This component operates silently in the background
  // It doesn't render any visible UI elements
  // All notification display is handled by the browser and fcmTokenManager
  // ================================================================

  return null;
}

/**
 * ================================================================
 * END OF COMPONENT: FirebaseNotificationHandler
 * ================================================================
 *
 * @exports          FirebaseNotificationHandler
 * @dependencies     useFirebaseNotifications hook, Firebase SDK
 * @usedBy           App.tsx or main layout components
 *
 * @features
 * - Silent operation: No UI rendering, works in background
 * - Auto-permission: Optional automatic permission request
 * - Error resilience: Graceful failure without app disruption
 * - Development logging: Helpful debugging information
 * - Integration ready: Works alongside existing notification systems
 *
 * @security
 * - Permission respect: Only requests permissions when appropriate
 * - Error isolation: Firebase errors don't affect main app
 * - Token management: Secure token handling through hooks
 *
 * ================================================================
 */
